// OAuth2/PKCE + KW token-exchange for the browser SPAs (viz/admin/cost).
//
// D-GOV-10 Phase 5: replaces bearer-token paste. Flow:
//   1. beginLogin()    — PKCE: gen verifier+state, fetch the IdP's OIDC config,
//                        return the authorize URL (caller does window.location = url).
//   2. <IdP login>     — IdP redirects back to redirectUri?code=…&state=…
//   3. completeLogin() — verify state, exchange code→IdP token (PKCE public client,
//                        no secret), then POST that token to KW's /v1/auth/oidc-token
//                        and return the **KW-self** access token (DB-baked realms).
//
// Framework-agnostic (no React); browser globals only (crypto.subtle, fetch,
// sessionStorage). Storage + fetch are injectable for tests.

export interface OAuthMode {
  issuer: string;
  jwks_url?: string;
  audience?: string;
  /**
   * D-GOV-11: per-UI OAuth client_id map (app-key → client_id), served by KW
   * /v1/discovery. The IdP (Zitadel/Entra) generates client_ids, so an SPA reads
   * its own here at runtime — there is no build-time default. The deployment must
   * populate ``identity.accepted_issuers[].clients`` (bundled Zitadel: the
   * provisioning script; external IdP: from the customer's app-registrations).
   */
  clients?: Record<string, string>;
}

/**
 * Resolve the OAuth client_id for an SPA from discovery — no fallback. Throws if
 * the instance does not advertise a client_id for this app, so a misconfigured
 * deployment fails loudly instead of sending a bogus client_id to the IdP.
 */
export function clientIdForApp(mode: OAuthMode, appKey: string): string {
  const id = mode.clients?.[appKey];
  if (!id) {
    throw new Error(
      `instance advertises no OAuth client_id for "${appKey}" — provision the IdP ` +
        "and set identity.accepted_issuers[].clients (D-GOV-11)",
    );
  }
  return id;
}

export interface OidcEndpoints {
  authorization_endpoint: string;
  token_endpoint: string;
}

export interface OAuthLoginConfig {
  /** The IdP issuer URL (from KW /v1/discovery auth_modes[i1].issuer). */
  issuer: string;
  /** Public PKCE client id registered for this SPA at the IdP. */
  clientId: string;
  /** This SPA's callback URL, registered at the IdP (e.g. https://admin.…/callback). */
  redirectUri: string;
  /** OIDC scopes to request (e.g. ["openid", "profile", "email", "groups"]). */
  scopes: string[];
}

export interface PkceStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

type FetchFn = typeof fetch;

const VERIFIER_KEY = "kw.oauth.verifier";
const STATE_KEY = "kw.oauth.state";

function _toB64Url(bytes: Uint8Array): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function _defaultStorage(): PkceStorage {
  // Browser SPAs only — sessionStorage survives the IdP redirect round-trip but
  // not a tab close. Lazily accessed so importing this module in a non-browser
  // context (tests inject storage) does not touch window.
  return (globalThis as unknown as { sessionStorage: PkceStorage }).sessionStorage;
}

/** Random 32-byte PKCE code-verifier, base64url (RFC 7636). */
export function generateCodeVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return _toB64Url(bytes);
}

/** S256 code-challenge for a verifier. */
export async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return _toB64Url(new Uint8Array(digest));
}

/** Random CSRF state token. */
export function randomState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return _toB64Url(bytes);
}

/** Read KW's discovery; return the I1 (OAuth) mode, or null if the instance offers none. */
export async function discoverOAuthMode(
  coreBaseUrl: string,
  fetchFn: FetchFn = fetch,
): Promise<OAuthMode | null> {
  const r = await fetchFn(`${coreBaseUrl}/discovery`);
  if (!r.ok) return null;
  const body = (await r.json()) as { auth_modes?: Array<Record<string, unknown>> };
  const i1 = (body.auth_modes ?? []).find((m) => m.mode === "i1");
  if (!i1) return null;
  return {
    issuer: String(i1.issuer),
    jwks_url: i1.jwks_url ? String(i1.jwks_url) : undefined,
    audience: i1.audience ? String(i1.audience) : undefined,
    clients:
      i1.clients && typeof i1.clients === "object"
        ? (i1.clients as Record<string, string>)
        : undefined,
  };
}

/** Fetch the IdP's OIDC discovery document for authorize/token endpoints. */
export async function fetchOidcEndpoints(
  issuer: string,
  fetchFn: FetchFn = fetch,
): Promise<OidcEndpoints> {
  const url = `${issuer.replace(/\/$/, "")}/.well-known/openid-configuration`;
  const r = await fetchFn(url);
  if (!r.ok) throw new Error(`OIDC discovery failed (${r.status}) at ${url}`);
  const cfg = (await r.json()) as Partial<OidcEndpoints>;
  if (!cfg.authorization_endpoint || !cfg.token_endpoint) {
    throw new Error("OIDC discovery missing authorization_endpoint/token_endpoint");
  }
  return {
    authorization_endpoint: cfg.authorization_endpoint,
    token_endpoint: cfg.token_endpoint,
  };
}

/** Start a PKCE login: stash verifier+state, return the IdP authorize URL to redirect to. */
export async function beginLogin(
  cfg: OAuthLoginConfig,
  opts: { storage?: PkceStorage; fetchFn?: FetchFn } = {},
): Promise<string> {
  const storage = opts.storage ?? _defaultStorage();
  const fetchFn = opts.fetchFn ?? fetch;
  const verifier = generateCodeVerifier();
  const state = randomState();
  const challenge = await pkceChallenge(verifier);
  storage.setItem(VERIFIER_KEY, verifier);
  storage.setItem(STATE_KEY, state);
  const endpoints = await fetchOidcEndpoints(cfg.issuer, fetchFn);
  const params = new URLSearchParams({
    response_type: "code",
    client_id: cfg.clientId,
    redirect_uri: cfg.redirectUri,
    scope: cfg.scopes.join(" "),
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  return `${endpoints.authorization_endpoint}?${params.toString()}`;
}

export interface CompleteLoginArgs {
  /** KW core API base (e.g. "/core/v1"). */
  coreBaseUrl: string;
  issuer: string;
  clientId: string;
  redirectUri: string;
  params: { code?: string | null; state?: string | null; error?: string | null };
  storage?: PkceStorage;
  fetchFn?: FetchFn;
}

/**
 * Complete the callback: verify state, exchange code→IdP token (PKCE), then
 * IdP token → KW-self token via POST /auth/oidc-token. Returns the KW access token.
 */
export async function completeLogin(args: CompleteLoginArgs): Promise<string> {
  const storage = args.storage ?? _defaultStorage();
  const fetchFn = args.fetchFn ?? fetch;
  const { code, state, error } = args.params;
  if (error) throw new Error(`IdP returned error: ${error}`);
  if (!code || !state) throw new Error("OAuth callback missing code/state");
  const expectedState = storage.getItem(STATE_KEY);
  if (!expectedState || expectedState !== state) {
    throw new Error("OAuth state mismatch (possible CSRF) — restart login");
  }
  const verifier = storage.getItem(VERIFIER_KEY);
  if (!verifier) throw new Error("OAuth verifier missing — restart login");

  const endpoints = await fetchOidcEndpoints(args.issuer, fetchFn);
  // 1. authorization code → IdP token (public PKCE client, no secret).
  const idpResp = await fetchFn(endpoints.token_endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      code,
      redirect_uri: args.redirectUri,
      client_id: args.clientId,
      code_verifier: verifier,
    }).toString(),
  });
  if (!idpResp.ok) throw new Error(`IdP token exchange failed (${idpResp.status})`);
  const idpTokens = (await idpResp.json()) as { access_token?: string; id_token?: string };
  // Send the id_token, not the access_token: the OIDC id_token is the identity
  // assertion that carries the subject_claim (email) + groups. Zitadel's JWT
  // access_token holds only sub/aud/scope — no email — so the KW exchange
  // (subject_claim=email, D-GOV-9) needs the id_token. Both have aud=client_id,
  // which the core verifier accepts via the issuer's provisioned clients map.
  if (!idpTokens.id_token) throw new Error("IdP token response missing id_token");

  // 2. IdP token → KW-self token (DB-baked realms, D-GOV-10).
  const kwResp = await fetchFn(`${args.coreBaseUrl}/auth/oidc-token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ idp_token: idpTokens.id_token }),
  });
  if (!kwResp.ok) throw new Error(`KW token exchange failed (${kwResp.status})`);
  const kw = (await kwResp.json()) as { access_token?: string };
  storage.removeItem(VERIFIER_KEY);
  storage.removeItem(STATE_KEY);
  if (!kw.access_token) throw new Error("KW token response missing access_token");
  return kw.access_token;
}
